import moment, { Moment } from 'moment';

import ErrorIcon from '@common/assets/icons/chat/error.svg?react';
import ReadedIcon from '@common/assets/icons/chat/readed.svg?react';
import SendingIcon from '@common/assets/icons/chat/sending.svg?react';
import SentIcon from '@common/assets/icons/chat/sent.svg?react';
import { CachedImage } from '@common/ui/CachedImage';
import { DynamicImage } from '@common/ui/DynamicImage';

import { type User } from '@services/UsersService';

import styles from './ChatCell.module.scss';

export type ChatCellProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  type?: 'my' | 'friend';
  status?: 'readed' | 'sended' | 'failed' | 'sending';
  lastMessageDate?: Moment;
  text?: string;
  unreadedMsg?: number;
  profile: User;
};

export const ChatCell: React.FC<ChatCellProps> = ({
  type,
  status,
  lastMessageDate,
  text,
  unreadedMsg,
  profile,
  ...props
}) => {
  return (
    <div {...props} className={styles.chatCellContainer}>
      <DynamicImage
        src={profile.photos[0]}
        className={styles.picture}
        loading="lazy"
        alt={`chat-avatar-${profile?.username}`}
        testId="chat-cell-avatar"
      />
      <div className={styles.content}>
        <div className={styles.topLine}>
          <span className={styles.username}>
            <span>
              {profile.username}, {profile.age}
            </span>
            {profile.is_premium && (
              <CachedImage
                testId="chat-cell-premium-badge"
                alt="badge"
                src="/icons/3d-icons/fire.png"
                blur="2px"
                style={{ width: '24px' }}
              />
            )}
            {profile.is_verified && (
              <CachedImage
                testId="chat-cell-verified-badge"
                alt="badge"
                src="/icons/3d-icons/confirmed.png"
                blur="2px"
                style={{ width: '24px' }}
              />
            )}
          </span>
          <span className={styles.timeContainer}>
            {lastMessageDate && (
              <span className={styles.time} data-test-id="chat-cell-time">
                {moment().isSame(lastMessageDate, 'day')
                  ? lastMessageDate.format('HH:mm')
                  : lastMessageDate.format('DD.MM HH:mm')}
              </span>
            )}
            <span className={styles.status}>
              {status === 'readed' && (
                <ReadedIcon data-test-id="chat-cell-status-readed" />
              )}
              {status === 'sended' && (
                <SentIcon data-test-id="chat-cell-status-sended" />
              )}
              {status === 'failed' && (
                <ErrorIcon data-test-id="chat-cell-status-failed" />
              )}
              {status === 'sending' && (
                <SendingIcon data-test-id="chat-cell-status-sending" />
              )}
            </span>
          </span>
        </div>
        <div className={styles.bottomLine}>
          <span
            className={styles.message}
            data-sentry-mask
            data-test-id="chat-cell-message"
          >
            {text && type === 'my' && (
              <span className={styles.isMe} data-test-id="chat-cell-prefix">
                Вы:
              </span>
            )}
            {text ||
              `У вас взаимная симпатия с ${profile.username}, вы можете начать общение`}
          </span>
          {!!unreadedMsg && unreadedMsg > 0 && (
            <span
              className={styles.messageCount}
              data-test-id="chat-cell-unread-count"
            >
              {' '}
              {unreadedMsg >= 100 ? '99+' : unreadedMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
